import { useLiveSnapshot } from "../context/LiveSnapshotContext";
import { Radio } from "lucide-react";
import { formatLiveMinute } from "../lib/liveMinute";

/**
 * <LiveOverlay fixtureId={...} compact /> — renders nothing when the match
 * isn't live, otherwise an animated red badge with current minute + score.
 *
 * Two variants:
 *   - default: full pill with "● LIVE · 1H 67' · 2-1"
 *   - compact: "● 67' 2-1" (for tight cards)
 */
export const LiveOverlay = ({ fixtureId, compact = false, className = "" }) => {
  const { getLive } = useLiveSnapshot();
  const live = getLive(fixtureId);
  if (!live) return null;

  const minute = formatLiveMinute(live.minute, live.minute_extra, live.status);
  const score = live.score_str || `${live.home_score}-${live.away_score}`;
  const homeReds = Number(live.home_red_cards || 0);
  const awayReds = Number(live.away_red_cards || 0);
  const totalReds = homeReds + awayReds;
  const redCardLabel = totalReds > 0
    ? (homeReds && awayReds
        ? `🟥 H${homeReds} A${awayReds}`
        : homeReds
        ? `🟥 Home${homeReds > 1 ? ` ×${homeReds}` : ""}`
        : `🟥 Away${awayReds > 1 ? ` ×${awayReds}` : ""}`)
    : null;

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-500/15 text-red-500 border border-red-500/40 text-[10px] font-bold tracking-wide ${className}`}
        data-testid={`live-overlay-${fixtureId}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
        {minute && <span className="font-tabular">{minute}</span>}
        <span className="font-tabular">{score}</span>
        {redCardLabel && (
          <span
            className="ml-0.5 text-[9px] tracking-wider"
            data-testid={`live-redcards-${fixtureId}`}
          >
            {redCardLabel}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 text-red-500 border border-red-500/40 text-[11px] font-bold tracking-wider ${className}`}
      data-testid={`live-overlay-${fixtureId}`}
    >
      <Radio className="w-3 h-3 animate-pulse" />
      LIVE
      {live.status && live.status !== "LIVE" && <span className="font-tabular">· {live.status}</span>}
      {minute && <span className="font-tabular">{minute}</span>}
      <span className="font-tabular text-foreground/80">· {score}</span>
      {redCardLabel && (
        <span
          className="font-tabular text-[10px] pl-1 border-l border-red-500/40"
          data-testid={`live-redcards-${fixtureId}`}
        >
          {redCardLabel}
        </span>
      )}
    </span>
  );
};

export default LiveOverlay;
